import { useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { Modal } from '../feedback/Modal';
import { Spinner } from '../feedback/LoadingState';
import { Button } from '../actions/Button';
import { SelectableChip } from '../forms/SelectableChip';
import { useTranslation } from 'react-i18next';
import { getActivities, getActivityCount, markActivitiesRead } from '../../services/activityService';
import { useProfileStore } from '../../store/useProfileStore';
import { useActivityListStore } from '../../store/useActivityListStore';

interface ActivityListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FETCH_SIZE = 20;

// Starter baseline: this modal renders only generic, non-order activity cards.
// The original core-app variant also rendered grouped "your listing's orders"
// sections via OrderGroupSection + ActivityOrderCard — those are feature-
// specific and were dropped along with the orders/listings surfaces. Per-app
// code that wants a richer feed can re-introduce its own section components
// and merge them in below the generic list.

async function loadData(profileId: string, status: 'new' | 'read', cursor?: string) {
  const store = useActivityListStore.getState();
  store.setLoading(true);
  try {
    const [countsNew, countsRead, results] = await Promise.all([
      getActivityCount(profileId, 'new'),
      getActivityCount(profileId, 'read'),
      getActivities(profileId, status, FETCH_SIZE, cursor),
    ]);
    useActivityListStore.getState().setUnreadCount(countsNew.count);
    useActivityListStore.getState().setReadCount(countsRead.count);
    if (cursor) {
      useActivityListStore.getState().appendActivities(results);
    } else {
      useActivityListStore.getState().setActivities(results);
    }
    useActivityListStore.getState().setHasMore(results.length === FETCH_SIZE);
    useActivityListStore.getState().setCursorId(results.at(-1)?.id);
  } finally {
    useActivityListStore.getState().setLoading(false);
  }
}

export function ActivityListModal({ isOpen, onClose }: ActivityListModalProps) {
  const { t } = useTranslation('activity');
  const router = useRouter();
  const { activities, unreadCount, readCount, loading, hasMore, cursorId, view } = useActivityListStore();
  const { setView, setActivities, setCursorId } = useActivityListStore.getState();

  useEffect(() => {
    if (!isOpen) return;
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    const { reset: resetList } = useActivityListStore.getState();
    resetList();
    loadData(profileId, 'new');
    return () => resetList();
  }, [isOpen]);

  const handleViewChange = (newView: 'new' | 'read') => {
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    setView(newView);
    setActivities([]);
    setCursorId(undefined);
    loadData(profileId, newView);
  };

  const handleLoadMore = () => {
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    loadData(profileId, view, cursorId);
  };

  const handleMarkAsRead = async (activityId: string) => {
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    await markActivitiesRead(profileId, [activityId]);
    await loadData(profileId, view);
  };

  const handleRoute = (route: string) => {
    onClose();
    router.history.push(route);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={t('modal.title')}
      footer={
        <Button size="sm" onClick={onClose}>
          {t('modal.done')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <SelectableChip active={view === 'new'} onClick={() => handleViewChange('new')}>
            {t('tabs.unread', { count: unreadCount })}
          </SelectableChip>
          <SelectableChip active={view === 'read'} onClick={() => handleViewChange('read')}>
            {t('tabs.read', { count: readCount })}
          </SelectableChip>
        </div>

        <div className="flex flex-col gap-3">
          {loading && activities.length === 0 ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : activities.length === 0 ? (
            <p className="m-0 py-8 text-center text-sm text-muted">
              {view === 'new' ? t('empty.unread') : t('empty.read')}
            </p>
          ) : (
            activities.map((activity) => (
              <div key={activity.id} className="relative rounded-app border border-panel-border p-4">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[.14em] text-muted">{activity.type}</span>
                  <span className="text-xs text-muted">{new Date(activity.createdAt).toLocaleString()}</span>
                </div>

                <p className="m-0 mb-1 text-sm font-semibold text-ink">{activity.message}</p>

                {activity.content && (
                  <div className="mb-2 text-sm text-muted" dangerouslySetInnerHTML={{ __html: activity.content }} />
                )}

                {activity.imageUrl && (
                  <img src={activity.imageUrl} alt="" className="mb-2 aspect-[4/3] w-full rounded-app object-cover" />
                )}

                {activity.displayProfiles?.length > 0 && (
                  <div className="mb-2 flex items-center gap-2">
                    {activity.displayProfiles.map((dp) => (
                      <div key={dp.profileId} className="flex items-center gap-1">
                        {dp.avatarUrl && (
                          <img src={dp.avatarUrl} alt={dp.displayName} className="h-5 w-5 rounded-full object-cover" />
                        )}
                        <span className="text-xs text-muted">{dp.displayName}</span>
                      </div>
                    ))}
                  </div>
                )}

                {activity.route && activity.route.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRoute(activity.route!)}
                    className="border-0 bg-transparent p-0 text-sm font-semibold text-link hover:underline"
                  >
                    {t('card.checkItOut')}
                  </button>
                )}

                {view === 'new' && (
                  <button
                    type="button"
                    onClick={() => handleMarkAsRead(activity.id)}
                    aria-label={t('card.markAsRead')}
                    title={t('card.markAsRead')}
                    className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded border-0 bg-transparent text-muted transition-colors hover:bg-[rgba(178,58,46,.12)] hover:text-danger"
                  >
                    &times;
                  </button>
                )}
              </div>
            ))
          )}

          {hasMore && !loading && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={handleLoadMore}
                className="border-0 bg-transparent text-sm font-semibold text-link hover:underline"
              >
                {t('loadMore')}
              </button>
            </div>
          )}

          {loading && activities.length > 0 && (
            <div className="flex justify-center py-2">
              <Spinner size={16} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
